import { Toggle } from '@fluentui/react';
import {
  IChartProps,
  ListRow,
  toggleStylesLabelOnly,
  useClassNames,
  WidgetPanel,
  widgetPanelStylesInfo,
} from '@h2oai/ui-kit';

import { Feature, FeatureProfile } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { FeatureType } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/FeatureSetProtoApi.pb';
import { ChartWidget } from '../ChartWidget/ChartWidget';
import { convertToFeatureListData, getFeatureColumns } from '../FeatureList/FeatureList';
import {
  featureDetailsStylesDefault,
  featureDetailsStylesFullScreen,
  IFeatureDetailsClassNames,
  IFeatureDetailsStyles,
  listRowStylesFeatureDetails,
} from './FeatureDetails.styles';

const getFieldGroups = (data: Feature) => {
  const specialDataFields: any[][] = [];
  const statisticsFields: any[][] = [];
  const categoricalFields: any[][] = [];
  const { specialData, profile: { statistics, categorical } = {} } = data;

  if (specialData) {
    specialDataFields.push(
      [
        'SPI',
        <Toggle
          styles={toggleStylesLabelOnly}
          key="spi"
          label={specialData.spi ? 'True' : 'False'}
          inlineLabel
          checked={specialData.spi}
        />,
      ],
      [
        'PCI',
        <Toggle
          key="pci"
          label={specialData.pci ? 'True' : 'False'}
          inlineLabel
          checked={specialData.pci}
          styles={toggleStylesLabelOnly}
        />,
      ],
      [
        'RPI',
        <Toggle
          key="rpi"
          label={specialData.rpi ? 'True' : 'False'}
          inlineLabel
          checked={specialData.rpi}
          styles={toggleStylesLabelOnly}
        />,
      ],
      [
        'Demographic',
        <Toggle
          key="demographic"
          label={specialData.demographic ? 'True' : 'False'}
          inlineLabel
          checked={specialData.demographic}
          styles={toggleStylesLabelOnly}
        />,
      ],
      [
        'Sensitive',
        <Toggle
          key="Sensitive"
          label={specialData.sensitive ? 'True' : 'False'}
          inlineLabel
          checked={specialData.sensitive}
          styles={toggleStylesLabelOnly}
        />,
      ]
    );
  }
  if (statistics) {
    statisticsFields.push(
      ['Max', `${statistics.max}`],
      ['Min', `${statistics.min}`],
      ['Median', `${statistics.median}`],
      ['Mean', `${statistics.mean}`],
      ['stddev', `${statistics.stddev}`],
      ['stddevRecCount', `${statistics.stddevRecCount}`],
      ['nullCount', `${statistics.nullCount}`],
      ['nanCount', `${statistics.nanCount}`],
      ['unique', `${statistics.unique}`]
    );
  }
  if (categorical) {
    const { unique, top = [] } = categorical;
    if (unique) {
      categoricalFields.push(['Unique', categorical.unique]);
    }
    top.forEach(({ name, count }) => categoricalFields.push([name, count]));
  }
  return {
    specialDataFields,
    statisticsFields,
    categoricalFields,
  };
};

const renderFields = (classNames: IFeatureDetailsClassNames, fields: any[][], fullScreen = false) => {
  const lastIndex = fields.length - 1;
  const groupItems: any[][] = [];
  const count = fullScreen ? 3 : 6;
  return (
    <div className={classNames.fieldGroup}>
      {fields.map((field, i) => {
        groupItems.push(field);
        if (groupItems.length === count || lastIndex === i) {
          const renderItems = groupItems.splice(0, groupItems.length);
          return (
            <div key={i} className={classNames.fields}>
              {renderItems.map((field) => {
                return (
                  <div key={field[0]} className={classNames.field}>
                    <div className={classNames.label} title={field[0]}>
                      {field[0]}
                    </div>
                    <div
                      className={classNames.value}
                      title={
                        typeof field[1] === 'string' || typeof field[1] === 'number' ? String(field[1]) : undefined
                      }
                    >
                      {field[1]}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

// Statistics are computed only for feature of type Numerical and Categorical
// statisticsAvailable shows it is ready or not
const getEmptyMessages = ({ featureType, statisticsAvailable }: FeatureProfile = {}) => {
  let statisticsMessage = 'No Data Available';
  let categoricalMessage = 'No Data Available';
  if (featureType === FeatureType.NUMERICAL) {
    statisticsMessage = statisticsAvailable ? '' : 'Awaiting Data...';
  }
  if (featureType === FeatureType.CATEGORICAL) {
    categoricalMessage = statisticsAvailable ? '' : 'Awaiting Data...';
  }
  return {
    statistics: statisticsMessage,
    categorical: categoricalMessage,
  };
};

const getEmptyMessageProps = (profile: FeatureProfile = {}) => {
  const messages = getEmptyMessages(profile);
  if (profile.featureType === FeatureType.CATEGORICAL) {
    return messages.categorical ? { message: messages.categorical } : undefined;
  } else {
    return messages.statistics ? { message: messages.statistics } : undefined;
  }
};

export const getChartData = (d: Feature, featureType: FeatureType) => {
  if (featureType === FeatureType.CATEGORICAL) {
    return (d.profile?.categorical?.top || []).map(({ name, count }) => ({
      name,
      count: +(count || 0),
    }));
  } else {
    const s = d.profile?.statistics || {};
    return [
      {
        name: 'Record Count',
        value: +(s.stddevRecCount || 0),
      },
      {
        name: 'Null Count',
        value: +(s.nullCount || 0),
      },
      {
        name: 'NaN Count',
        value: +(s.nanCount || 0),
      },
      {
        name: 'Unique Count',
        value: +(s.unique || 0),
      },
    ];
  }
};

const getChartProps = (d: Feature, featureType: FeatureType): IChartProps => {
  return {
    data: getChartData(d, featureType),
    labelFieldName: 'name',
    valueFieldNames: [featureType === FeatureType.CATEGORICAL ? 'count' : 'value'],
    chartProps: {
      colors: ['#25C4FE80', '#00316A'],
      gradient: { start: '#00316A', end: '#25C4FE' },
    },
  };
};

interface IFeatureDetailsProps {
  data: Feature;
  fullScreen?: boolean;
  watchWidth?: number;
  onOpenFeaturePage?: (data: Feature) => void;
}

export const FeatureDetails = ({ data, fullScreen }: IFeatureDetailsProps) => {
  const featureType = data.profile?.featureType;
  const isNumericOrCategorical = featureType === FeatureType.CATEGORICAL || featureType === FeatureType.NUMERICAL;
  const classNames = useClassNames<IFeatureDetailsStyles, IFeatureDetailsClassNames>(
    'FeatureDetails',
    featureDetailsStylesDefault,
    fullScreen ? featureDetailsStylesFullScreen : undefined
  );
  const fields = getFieldGroups(data);
  return (
    <div className={classNames.root} data-test="feature-details">
      <div className={classNames.contents}>
        {fullScreen && (
          <div className={classNames.rows}>
            <ListRow
              styles={listRowStylesFeatureDetails}
              data={convertToFeatureListData(data)}
              columns={getFeatureColumns(false, false)}
            />
            <ListRow
              styles={listRowStylesFeatureDetails}
              data={convertToFeatureListData(data)}
              columns={getFeatureColumns(false, true)}
            />
          </div>
        )}
        <div className={classNames.panels}>
          <WidgetPanel
            styles={widgetPanelStylesInfo}
            style={
              fullScreen
                ? { marginBottom: isNumericOrCategorical ? 24 : 0 }
                : { width: isNumericOrCategorical ? 208 : '100%', marginRight: 16 }
            }
            title="Special Data"
          >
            {renderFields(classNames, fields.specialDataFields, fullScreen)}
          </WidgetPanel>
          {isNumericOrCategorical && (
            <div className={classNames.flexPanel}>
              {fullScreen && (
                <ChartWidget
                  title={featureType === FeatureType.CATEGORICAL ? 'Categorical Statistics' : 'Statistics'}
                  chartProps={getChartProps(data, featureType)}
                  emptyMessageProps={getEmptyMessageProps(data.profile)}
                />
              )}
              <WidgetPanel
                styles={widgetPanelStylesInfo}
                style={fullScreen ? { width: '100%' } : undefined}
                title={featureType === FeatureType.CATEGORICAL ? 'Categorical Statistics' : 'Statistics'}
                emptyMessageProps={getEmptyMessageProps(data.profile)}
              >
                {renderFields(
                  classNames,
                  featureType === FeatureType.CATEGORICAL ? fields.categoricalFields : fields.statisticsFields,
                  fullScreen
                )}
              </WidgetPanel>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
